"use strict";
/**
 * Firestore Service Layer for App Flags
 *
 * This module provides Firestore integration for storing and retrieving
 * flag context data. It follows the context propagation model where:
 * - User context is stored per-user (1:1 with user)
 * - System context is stored per-environment (shared)
 * - Flag configs are stored globally (environment is an input to evaluation, not a collection key)
 * - Segments are stored globally
 *
 * The evaluation engine runs on-demand using this context data.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_FLAGS_COLLECTIONS = void 0;
exports.getUserContextPath = getUserContextPath;
exports.getSystemContextPath = getSystemContextPath;
exports.getFlagConfigPath = getFlagConfigPath;
exports.getSegmentPath = getSegmentPath;
exports.getFlagsCollectionPath = getFlagsCollectionPath;
exports.getSegmentsCollectionPath = getSegmentsCollectionPath;
exports.createStoredUserContext = createStoredUserContext;
exports.createStoredSystemContext = createStoredSystemContext;
/**
 * Firestore collection paths for app flags data
 *
 * All admin collections are under the /internal namespace which has
 * security rules that allow read/write for users with the internal claim.
 *
 * Note: Firestore document references must have an even number of path segments.
 * Collection references have odd segments, document references have even segments.
 */
exports.APP_FLAGS_COLLECTIONS = {
    /** User-level context inputs: internal/appFlags/userContext/{userUid} */
    userContext: "internal/appFlags/userContext",
    /** System/environment context: internal/appFlags/systemContext/{environment} */
    systemContext: "internal/appFlags/systemContext",
    /** Flag configurations (global, not per-environment): internal/appFlags/flagConfigs/{flagKey} */
    flagConfigs: "internal/appFlags/flagConfigs",
    /** Segment definitions (global, not per-environment): internal/appFlags/segments/{segmentId} */
    segments: "internal/appFlags/segments",
};
/**
 * Helper to get the user context document path
 */
function getUserContextPath(userUid) {
    return `${exports.APP_FLAGS_COLLECTIONS.userContext}/${userUid}`;
}
/**
 * Helper to get the system context document path
 */
function getSystemContextPath(environment) {
    return `${exports.APP_FLAGS_COLLECTIONS.systemContext}/${environment}`;
}
/**
 * Helper to get the flag config document path
 *
 * Note: Environment is NOT part of the path - flag configs are global.
 * Environment-specific behavior is expressed via targeting rules.
 */
function getFlagConfigPath(flagKey) {
    return `${exports.APP_FLAGS_COLLECTIONS.flagConfigs}/${flagKey}`;
}
/**
 * Helper to get the segment definition document path
 *
 * Note: Environment is NOT part of the path - segments are global.
 * Environment-specific behavior is expressed via conditions.
 */
function getSegmentPath(segmentUid) {
    return `${exports.APP_FLAGS_COLLECTIONS.segments}/${segmentUid}`;
}
/**
 * Helper to get the flags collection path
 *
 * Note: This is a global collection, not per-environment.
 */
function getFlagsCollectionPath() {
    return exports.APP_FLAGS_COLLECTIONS.flagConfigs;
}
/**
 * Helper to get the segments collection path
 *
 * Note: This is a global collection, not per-environment.
 */
function getSegmentsCollectionPath() {
    return exports.APP_FLAGS_COLLECTIONS.segments;
}
/**
 * Create an initial user context object
 */
function createStoredUserContext(userUid, attributes = {}) {
    return {
        userUid,
        organizationUids: [],
        customAttributes: {},
        contextVersion: 1,
        updatedAt: Date.now(),
        ...attributes,
    };
}
/**
 * Create an initial system context object
 */
function createStoredSystemContext(environment) {
    return {
        environment,
        configVersion: 1,
        segmentsVersion: 1,
        updatedAt: Date.now(),
        customAttributes: {},
    };
}
//# sourceMappingURL=firestore.js.map